import {
  SettlementFilters,
  SettlementListFilters
} from '@/apis/settlement/interface';
import api from '@/apis/settlement';
import { useMutation, useQuery } from 'react-query';
import { authState } from '@/recoil/auth';
import { useRecoilValue } from 'recoil';
import GlobalAlert from '@/components/GlobalAlert';
import { AxiosError } from 'axios';
import { errors } from '@/constants/errors';
import { ExcelDownload } from '@/utils/excel';
import {
  PaginationCriteriaInput,
  PharmacyCostReimbursementRequestInput,
  PharmacyCostReimbursementSummaryRequestInput
} from '@/api/bff_types';
import {
  getCostReimbursement,
  getCostReimbursementSummary
} from '@/utils/graphqlApis';

const useSettlementQuery = () => {
  const { token } = useRecoilValue(authState);

  /** [정산] 정산 목록 조회 */
  const settlementListQuery = (params: SettlementListFilters) => {
    const { from, to, phone, username, page, limit } = params;
    return useQuery(
      ['settlementList', from, to, phone, username, page, limit],
      () => api.getOrdersSettlements(token, params),
      {
        enabled: !!token,
        keepPreviousData: true,
        staleTime: 5000
      }
    );
  };

  /** [정산] 정산 요약 조회 (금액) */
  const settlementSummaryQuery = (params: SettlementFilters) => {
    const { from, to, phone, username } = params;
    return useQuery(
      ['settlementSummary', from, to, phone, username],
      () => api.getSettlementsSummary(token, params),
      {
        enabled: !!token,
        keepPreviousData: true,
        staleTime: 5000
      }
    );
  };

  /** [정산] 정산 요약 조회 (금액) */
  const settlementCountByTypeQuery = (params: SettlementFilters) => {
    const { from, to, phone, username } = params;
    return useQuery(
      ['settlementCountByType', from, to, phone, username],
      () => api.getSettlementsCountbyType(token, params),
      {
        enabled: !!token,
        keepPreviousData: true,
        staleTime: 5000
      }
    );
  };

  /** [정산] 정산 내역 엑셀 다운로드 */
  const settlementDownloadMutation = useMutation(
    (params: SettlementFilters) => api.onDownloadExcel(token, params),
    {
      onSuccess: (res: any, variables: SettlementFilters) => {
        ExcelDownload(res, variables);
      },
      onError: async (err: AxiosError) => {
        const errCode = JSON.parse(await err.response?.data?.text()).code;
        GlobalAlert.show({ title: errors[errCode] });
      }
    }
  );

  /** [정산] 총 변동 비용 및 변동 목록 총 갯수 조회 */
  const settlementVarianceSummaryQuery = (
    request: PharmacyCostReimbursementSummaryRequestInput
  ) => {
    const { createdAtTo, createdAtFrom } = request;
    return useQuery(
      ['varianceSummary', createdAtTo, createdAtFrom],
      () => getCostReimbursementSummary(token, request),
      {
        enabled: !!token
      }
    );
  };

  /** [정산] 변동 비용 목록 조회 */
  const settlementVarianceListQuery = (
    request: PharmacyCostReimbursementRequestInput,
    paginationCriteria: PaginationCriteriaInput
  ) => {
    const { createdAtTo, createdAtFrom } = request;
    const { size, pivotId } = paginationCriteria;
    return useQuery(
      ['varianceList', createdAtTo, createdAtFrom, size, pivotId],
      () => getCostReimbursement(token, request, paginationCriteria),
      {
        enabled: !!token,
        keepPreviousData: true,
        staleTime: 5000
      }
    );
  };

  return {
    settlementListQuery,
    settlementSummaryQuery,
    settlementCountByTypeQuery,
    settlementDownloadMutation,
    settlementVarianceListQuery,
    settlementVarianceSummaryQuery
  };
};

export default useSettlementQuery;
